import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import LoadingSpinner from '../../common/LoadingSpinner.jsx';
import { buildApiUrl, freshFetchInit } from '../../../utils/apiBase.js';
import { useRefreshHubSessionOnMount } from '../../../hooks/useRefreshHubSessionOnMount.js';
import { useBeta } from '../../../contexts/BetaContext.jsx';
import { useToast } from '../../../contexts/ToastContext.jsx';
import './InvitationCode.css';

const InvitationCode = () => {
  const { user, clinic } = useOutletContext();
  const { effectiveIsBeta, isHubSettingsReady, betaApiHeaders } = useBeta();
  const { showToast } = useToast();

  const [invLoading, setInvLoading] = useState(true);
  const [rewardsLoading, setRewardsLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [invData, setInvData] = useState(null);
  const [rewards, setRewards] = useState([]);
  const [generateMsg, setGenerateMsg] = useState({ type: '', text: '' });
  const [registerMsg, setRegisterMsg] = useState({ type: '', text: '' });
  const [redeemMsg, setRedeemMsg] = useState({ type: '', text: '' });

  const [registerCode, setRegisterCode] = useState('');
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupError, setLookupError] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  const [redeemConfirm, setRedeemConfirm] = useState(null);
  const [showInvitedList, setShowInvitedList] = useState(false);
  const [paymentInfoRequired, setPaymentInfoRequired] = useState(false);

  useRefreshHubSessionOnMount();

  const isSelfCode = invData?.code && registerCode.trim().toUpperCase() === invData.code;

  const fetchInvitation = useCallback(async () => {
    try {
      setInvLoading(true);
      const res = await fetch(buildApiUrl('/api/clinic/invitation'), freshFetchInit());
      const data = await res.json();
      if (res.ok) setInvData(data);
    } catch {
      /* ignore */
    } finally {
      setInvLoading(false);
    }
  }, []);

  const fetchRewards = useCallback(async () => {
    try {
      setRewardsLoading(true);
      const res = await fetch(buildApiUrl('/api/clinic/invitation/rewards'), freshFetchInit());
      if (!res.ok) return;
      const data = await res.json();
      setRewards(data.rewards || []);
    } catch {
      /* ignore */
    } finally {
      setRewardsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInvitation();
    fetchRewards();
  }, [fetchInvitation, fetchRewards]);

  const handleGenerate = async () => {
    setGenerateMsg({ type: '', text: '' });
    setActionLoading(true);
    try {
      const res = await fetch(buildApiUrl('/api/clinic/invitation/generate'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || '発行に失敗しました');
      setGenerateMsg({ type: 'success', text: '招待コードを発行しました' });
      showToast('invitation.generate.success');
      await fetchInvitation();
    } catch (err) {
      setGenerateMsg({ type: 'error', text: err.message });
      showToast('invitation.error', { message: err.message });
    } finally {
      setActionLoading(false);
    }
  };

  const handleLookup = async () => {
    if (!registerCode.trim() || isSelfCode) return;
    setLookupError('');
    setLookupResult(null);
    setLookupLoading(true);
    try {
      const res = await fetch(buildApiUrl(`/api/clinic/invitation/lookup?code=${encodeURIComponent(registerCode.trim())}`), {
        credentials: 'include'
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'コードが見つかりません');
      const myInvitedIds = invData?.invitedClinicIds || [];
      if (myInvitedIds.includes(data.clinicId)) {
        setLookupError('あなたが招待したクリニックの招待コードは使用できません');
        return;
      }
      setLookupResult(data);
      setShowConfirm(true);
    } catch (err) {
      setLookupError(err.message);
    } finally {
      setLookupLoading(false);
    }
  };

  const handleRegister = async () => {
    setRegisterMsg({ type: '', text: '' });
    setActionLoading(true);
    try {
      const res = await fetch(buildApiUrl('/api/clinic/invitation/register'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ code: registerCode.trim() })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || '登録に失敗しました');
      setRegisterMsg({ type: 'success', text: `招待コードを登録しました（招待元: ${data.inviterClinicName}）` });
      showToast('invitation.register.success', {
        message: `招待コードを登録しました（招待元: ${data.inviterClinicName}）`
      });
      setShowConfirm(false);
      setRegisterCode('');
      setLookupResult(null);
      await fetchInvitation();
    } catch (err) {
      setRegisterMsg({ type: 'error', text: err.message });
      showToast('invitation.error', { message: err.message });
    } finally {
      setActionLoading(false);
    }
  };

  const handleRedeem = async (reward) => {
    setRedeemMsg({ type: '', text: '' });
    setPaymentInfoRequired(false);
    setActionLoading(true);
    try {
      const res = await fetch(buildApiUrl('/api/clinic/invitation/redeem'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...betaApiHeaders },
        credentials: 'include',
        body: JSON.stringify({ rewardId: reward.id })
      });
      const data = await res.json();
      if (!res.ok) {
        // 課金系エラー: ポイントは消費していないので決済・請求へ誘導して終了する
        if (data.error === 'payment-info-required' || data.error === 'payment-incomplete') {
          setPaymentInfoRequired(true);
          const fallback = data.error === 'payment-incomplete'
            ? 'お支払いを完了できませんでした。支払い方法をご確認のうえ、再度お試しください。'
            : 'お支払い方法の登録が必要です';
          setRedeemMsg({ type: 'error', text: data.message || fallback });
          showToast('invitation.error', {
            message: data.message || fallback
          });
          setRedeemConfirm(null);
          return;
        }
        throw new Error(data.message || '交換に失敗しました');
      }
      setRedeemMsg({ type: 'success', text: data.message });
      showToast('invitation.redeem.success', { message: data.message });
      setRedeemConfirm(null);
      await Promise.all([fetchInvitation(), fetchRewards()]);
    } catch (err) {
      setRedeemMsg({ type: 'error', text: err.message });
      showToast('invitation.error', { message: err.message });
    } finally {
      setActionLoading(false);
    }
  };

  const handleCopyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopySuccess(true);
      showToast('clipboard.copy.success');
      setTimeout(() => setCopySuccess(false), 2000);
    } catch {
      /* ignore */
    }
  };

  const points = invData?.points || 0;
  const hasCode = invData?.code;
  const isRegistered = !!invData?.referredByCode;

  const renderAlert = (msg) => {
    if (!msg.text) return null;
    const isSuccess = msg.type === 'success';
    return (
      <div className={`inv-alert ${isSuccess ? 'inv-alert-success' : 'inv-alert-error'}`}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
          {isSuccess
            ? <polyline points="20 6 9 17 4 12"/>
            : <><circle cx="12" cy="12" r="9"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></>}
        </svg>
        {msg.text}
      </div>
    );
  };

  const sectionSpinner = (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--hub-space-2xl)' }}>
      <LoadingSpinner size="large" />
    </div>
  );

  if (!isHubSettingsReady) {
    return (
      <>
        <div className="hub-main-header">
          <h1 className="hub-page-title">招待コード</h1>
        </div>
        <div className="hub-main-content hub-enter-panel" data-panel-index="0">
          {sectionSpinner}
        </div>
      </>
    );
  }

  if (effectiveIsBeta) {
    return (
      <>
        <div className="hub-main-header">
          <h1 className="hub-page-title">招待コード</h1>
        </div>
        <div className="hub-main-content">
          <div className="hub-card hub-enter-panel" data-panel-index="0">
            <p style={{ marginBottom: 'var(--hub-space-lg)', color: 'var(--hub-text-secondary)' }}>
              β版期間中はご利用いただけません。
            </p>
            <Link to="/dashboard" className="hub-btn hub-btn-primary">
              ダッシュボードへ戻る
            </Link>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="hub-main-header">
        <h1 className="hub-page-title">招待コード</h1>
      </div>

      <div className="hub-main-content">
        {/* 招待コード発行セクション */}
        <div className="hub-card hub-enter-panel animate-fade-in-up stagger-1 inv-section" data-panel-index="0">
          {renderAlert(generateMsg)}

          <h2 className="inv-section-title">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="22" height="22">
              <path d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"/>
            </svg>
            あなたの招待コード
          </h2>

          {invLoading ? sectionSpinner : (
            <>
              {hasCode ? (
                <div className="inv-code-display">
                  <span className="inv-code-value">{invData.code}</span>
                  <button
                    type="button"
                    className="inv-copy-btn"
                    onClick={() => handleCopyCode(invData.code)}
                    title="コピー"
                  >
                    {copySuccess ? (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                        <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
                      </svg>
                    )}
                    {copySuccess ? 'コピー済み' : 'コピー'}
                  </button>
                </div>
              ) : (
                <div className="inv-generate-area">
                  <p className="inv-description">
                    招待コードを発行して、他のクリニックに共有しましょう。
                    <br />
                    招待した側・された側の両方にポイントが付与されます。
                  </p>
                  <button
                    type="button"
                    className="hub-btn hub-btn-primary"
                    onClick={handleGenerate}
                    disabled={actionLoading}
                  >
                    {actionLoading ? '発行中...' : '招待コードを発行'}
                  </button>
                </div>
              )}

              <div className="inv-stats">
                <div className="inv-stat-item">
                  <span className="inv-stat-label">保有ポイント</span>
                  <span className="inv-stat-value inv-stat-points">{points}<small>pt</small></span>
                </div>
                <div className="inv-stat-item inv-stat-invited">
                  <div className="inv-stat-label-row">
                    <span className="inv-stat-label">招待したクリニック数</span>
                    {(invData?.invitedCount || 0) > 0 && (
                      <button
                        type="button"
                        className={`inv-toggle-btn ${showInvitedList ? 'inv-toggle-open' : ''}`}
                        onClick={() => setShowInvitedList(prev => !prev)}
                        aria-label={showInvitedList ? '一覧を閉じる' : '一覧を開く'}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="16" height="16">
                          <polyline points="6 9 12 15 18 9"/>
                        </svg>
                      </button>
                    )}
                  </div>
                  <span className="inv-stat-value">{invData?.invitedCount || 0}</span>
                </div>
              </div>

              {showInvitedList && invData?.invitedClinics?.length > 0 && (
                <div className="inv-invited-list">
                  {invData.invitedClinics.map((c) => (
                    <div key={c.id} className="inv-invited-item">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                        <polyline points="9 22 9 12 15 12 15 22"/>
                      </svg>
                      <span className="inv-invited-name">{c.name}</span>
                      {c.invitedAt && (
                        <span className="inv-invited-date">{new Date(c.invitedAt).toLocaleDateString('ja-JP')}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* 招待コード登録セクション */}
        <div className="hub-card hub-enter-panel animate-fade-in-up stagger-2 inv-section" data-panel-index="1">
          {renderAlert(registerMsg)}

          <h2 className="inv-section-title">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="22" height="22">
              <path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
              <circle cx="8.5" cy="7" r="4"/>
              <line x1="20" y1="8" x2="20" y2="14"/>
              <line x1="23" y1="11" x2="17" y2="11"/>
            </svg>
            招待コードを登録
          </h2>

          {invLoading ? sectionSpinner : (
            <>
              {isRegistered ? (
                <div className="inv-registered-info">
                  <div className="inv-registered-badge">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    登録済み
                  </div>
                  <div className="inv-registered-detail">
                    <span className="inv-registered-label">招待元</span>
                    <span className="inv-registered-value">{invData.referredByClinicName}</span>
                    <span className="inv-registered-code">{invData.referredByCode}</span>
                  </div>
                </div>
              ) : (
                <>
                  <p className="inv-description">招待を受けた場合は、相手のクリニックの招待コードを入力して登録してください。登録は一度のみで、変更はできません。</p>
                  <div className="inv-register-form">
                    {isSelfCode && (
                      <p className="inv-self-code-warning">自分自身の招待コードは登録できません</p>
                    )}
                    <div className="inv-input-group">
                      <input
                        type="text"
                        className={`inv-input ${isSelfCode ? 'inv-input-error' : ''}`}
                        placeholder="INV-XXXXX"
                        value={registerCode}
                        onChange={(e) => {
                          setRegisterCode(e.target.value.toUpperCase());
                          setLookupError('');
                          setLookupResult(null);
                          setShowConfirm(false);
                        }}
                        maxLength={9}
                        disabled={actionLoading}
                      />
                      <button
                        type="button"
                        className="hub-btn hub-btn-secondary"
                        onClick={handleLookup}
                        disabled={!registerCode.trim() || isSelfCode || lookupLoading || actionLoading}
                      >
                        {lookupLoading ? '確認中...' : '確認'}
                      </button>
                    </div>
                    {lookupError && <p className="inv-lookup-error">{lookupError}</p>}
                  </div>

                  {showConfirm && lookupResult && (
                    <div className="inv-confirm-area">
                      <div className="inv-confirm-card">
                        <p className="inv-confirm-label">以下のクリニックの招待コードを登録しますか？</p>
                        <p className="inv-confirm-clinic-name">{lookupResult.clinicName}</p>
                        <p className="inv-confirm-notice">※ 登録後の変更はできません</p>
                        <div className="inv-confirm-actions">
                          <button
                            type="button"
                            className="hub-btn hub-btn-secondary"
                            onClick={() => { setShowConfirm(false); setLookupResult(null); }}
                            disabled={actionLoading}
                          >
                            キャンセル
                          </button>
                          <button
                            type="button"
                            className="hub-btn hub-btn-primary"
                            onClick={handleRegister}
                            disabled={actionLoading}
                          >
                            {actionLoading ? '登録中...' : '登録する'}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>

        {/* 特典交換セクション */}
        <div className="hub-card hub-enter-panel animate-fade-in-up stagger-3 inv-section" data-panel-index="2">
          <h2 className="inv-section-title">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="22" height="22">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
            </svg>
            ポイント特典
          </h2>
          {renderAlert(redeemMsg)}
          {paymentInfoRequired && (
            <div className="inv-alert inv-alert-error" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 'var(--hub-space-sm)' }}>
              <span>お支払い方法の確認・登録が必要です。決済・請求ページをご確認ください。</span>
              <Link to="/billing" className="hub-btn hub-btn-primary" style={{ alignSelf: 'flex-start' }}>
                決済・請求ページで確認する
              </Link>
            </div>
          )}

          {rewardsLoading ? sectionSpinner : rewards.length > 0 ? (
            <>
              {points > 0 ? (
                <p className="inv-description">保有ポイントで以下の特典と交換できます。</p>
              ) : (
                <p className="inv-description">招待を行いポイントを獲得すると、以下の特典と交換できるようになります。</p>
              )}
              <div className="inv-rewards-grid">
                {rewards.map((reward) => (
                  <div key={reward.id} className={`inv-reward-card ${points < reward.pointsCost || reward.productAvailable === false ? 'inv-reward-disabled' : ''}`}>
                    <div className="inv-reward-header">
                      <span className="inv-reward-cost-circle">{reward.pointsCost}<small>pt</small></span>
                      <span className="inv-reward-name">{reward.name.split('\n').map((line, i) => (
                        <React.Fragment key={i}>{i > 0 && <br />}{line}</React.Fragment>
                      ))}</span>
                    </div>
                    {reward.productAvailable === false && (
                      <p className="inv-reward-unavailable">現在こちらの特典は利用できません</p>
                    )}
                    {reward.description && (
                      <p className="inv-reward-description">{reward.description.split('\n').map((line, i) => (
                        <React.Fragment key={i}>{i > 0 && <br />}{line}</React.Fragment>
                      ))}</p>
                    )}
                    <button
                      type="button"
                      className="hub-btn hub-btn-primary inv-reward-btn"
                      disabled={points < reward.pointsCost || reward.productAvailable === false || actionLoading}
                      onClick={() => setRedeemConfirm(reward)}
                    >
                      交換する
                    </button>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="inv-description" style={{ marginBottom: 0 }}>招待を行いポイントを獲得すると、特典と交換できるようになります。</p>
          )}
        </div>

        {/* 交換履歴 */}
        {!invLoading && invData?.rewardHistory?.length > 0 && (
          <div className="hub-card hub-enter-panel animate-fade-in-up stagger-4 inv-section" data-panel-index="3">
            <h2 className="inv-section-title">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="22" height="22">
                <circle cx="12" cy="12" r="9"/>
                <polyline points="12 6 12 12 16 14"/>
              </svg>
              交換履歴
            </h2>
            <div className="inv-history-list">
              {invData.rewardHistory.map((h) => {
                const isPending = h.status === 'pending';
                return (
                  <div
                    key={h.id}
                    className={`inv-history-item${isPending ? ' inv-history-item-pending' : ''}`}
                  >
                    <span className="inv-history-name">
                      {h.rewardName}
                      {isPending && (
                        <span className="inv-history-badge-pending" title="交換処理が完了していません。数分以内に反映される見込みです。">
                          処理中
                        </span>
                      )}
                    </span>
                    <span className="inv-history-points">-{h.pointsUsed}pt</span>
                    <span className="inv-history-date">
                      {h.redeemedAt?._seconds
                        ? new Date(h.redeemedAt._seconds * 1000).toLocaleDateString('ja-JP')
                        : '—'}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* 特典交換確認モーダル */}
      {redeemConfirm && (
        <div className="inv-modal-overlay">
          <div className="inv-modal">
            <h3 className="inv-modal-title">特典の交換確認</h3>
            <p>以下の特典を交換しますか？</p>
            <div className="inv-modal-detail">
              <p className="inv-modal-reward-name">{redeemConfirm.name}</p>
              <p className="inv-modal-reward-cost">{redeemConfirm.pointsCost}pt 消費</p>
              <p className="inv-modal-reward-remaining">交換後の残りポイント: {points - redeemConfirm.pointsCost}pt</p>
            </div>
            <div className="inv-modal-actions">
              <button
                type="button"
                className="hub-btn hub-btn-secondary"
                onClick={() => setRedeemConfirm(null)}
                disabled={actionLoading}
              >
                キャンセル
              </button>
              <button
                type="button"
                className="hub-btn hub-btn-primary"
                onClick={() => handleRedeem(redeemConfirm)}
                disabled={actionLoading}
              >
                {actionLoading ? '交換中...' : '交換する'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default InvitationCode;
